import React from 'react';
import { 
  Shield, 
  CheckCircle, 
  AlertCircle,
  Info,
  Download,
  Calendar,
  FileText
} from 'lucide-react';

interface ComplianceControl {
  id: string;
  name: string;
  description: string;
  status: 'compliant' | 'in-progress' | 'non-compliant';
  lastAudit: string;
  evidence: string[];
}

const ComplianceReport: React.FC = () => {
  const controls: ComplianceControl[] = [
    {
      id: 'CC1.1',
      name: 'Security Policies',
      description: 'Organization has established security policies and procedures',
      status: 'compliant',
      lastAudit: '2024-01-15',
      evidence: ['Security Policy v2.1', 'Policy Training Records']
    },
    {
      id: 'CC1.2',
      name: 'Risk Assessment',
      description: 'Regular risk assessments are performed',
      status: 'compliant',
      lastAudit: '2024-01-20',
      evidence: ['Q4 Risk Assessment Report', 'Risk Matrix']
    },
    {
      id: 'CC2.1',
      name: 'Access Control',
      description: 'Access to systems is appropriately restricted',
      status: 'compliant',
      lastAudit: '2024-01-10',
      evidence: ['Access Control Matrix', 'User Access Reviews']
    },
    {
      id: 'CC3.1',
      name: 'Data Encryption',
      description: 'Sensitive data is encrypted at rest and in transit',
      status: 'compliant',
      lastAudit: '2024-01-25',
      evidence: ['Encryption Standards', 'TLS Configuration']
    },
    {
      id: 'CC4.1',
      name: 'Incident Response',
      description: 'Incident response procedures are documented and tested',
      status: 'in-progress',
      lastAudit: '2024-01-18',
      evidence: ['Incident Response Plan', 'Test Results']
    },
    {
      id: 'CC5.1',
      name: 'Backup and Recovery',
      description: 'Regular backups with tested recovery procedures',
      status: 'compliant',
      lastAudit: '2024-01-22',
      evidence: ['Backup Policy', 'Recovery Test Log']
    }
  ];

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'compliant': return 'text-green-600 bg-green-50';
      case 'in-progress': return 'text-yellow-600 bg-yellow-50';
      case 'non-compliant': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status: string) => {
    switch(status) {
      case 'compliant': return CheckCircle;
      case 'in-progress': return AlertCircle;
      case 'non-compliant': return AlertCircle;
      default: return Info;
    }
  };

  const complianceScore = Math.round(
    (controls.filter(c => c.status === 'compliant').length / controls.length) * 100
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-libre">SOC2 Compliance Report</h1>
          <p className="text-gray-600 mt-1">Track and maintain compliance with SOC2 Type II requirements</p>
        </div>
        <button className="inline-flex items-center px-4 py-2 bg-niwt-primary text-white rounded-lg hover:bg-niwt-primary/90 transition-colors">
          <Download className="w-5 h-5 mr-2" />
          Export Report
        </button>
      </div>

      {/* Compliance Score Card */}
      <div className="bg-gradient-to-r from-niwt-primary to-niwt-secondary rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold mb-2">Overall Compliance Score</h2>
            <div className="flex items-baseline">
              <span className="text-4xl font-bold">{complianceScore}%</span>
              <span className="ml-2 text-white/80">Compliant</span>
            </div>
            <p className="text-white/80 mt-2">
              {controls.filter(c => c.status === 'compliant').length} of {controls.length} controls met
            </p>
          </div>
          <Shield className="w-16 h-16 text-white/20" />
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
            <span className="text-2xl font-bold text-gray-900">
              {controls.filter(c => c.status === 'compliant').length}
            </span>
          </div>
          <h3 className="font-medium text-gray-900">Compliant Controls</h3>
          <p className="text-sm text-gray-600 mt-1">Meeting all requirements</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <AlertCircle className="w-8 h-8 text-yellow-600" />
            <span className="text-2xl font-bold text-gray-900">
              {controls.filter(c => c.status === 'in-progress').length}
            </span>
          </div>
          <h3 className="font-medium text-gray-900">In Progress</h3>
          <p className="text-sm text-gray-600 mt-1">Remediation underway</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <Calendar className="w-8 h-8 text-purple-600" />
            <span className="text-2xl font-bold text-gray-900">Q1 2024</span>
          </div>
          <h3 className="font-medium text-gray-900">Last Audit</h3>
          <p className="text-sm text-gray-600 mt-1">Next audit: Q2 2024</p>
        </div>
      </div>

      {/* Controls Table */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 font-libre">Control Status</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Control ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Control Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Audit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Evidence
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {controls.map(control => {
                const StatusIcon = getStatusIcon(control.status);
                return (
                  <tr key={control.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {control.id}
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{control.name}</div>
                        <div className="text-sm text-gray-500">{control.description}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(control.status)}`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {control.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {control.lastAudit}
                    </td>
                    <td className="px-6 py-4">
                      <button className="inline-flex items-center text-sm text-niwt-primary hover:text-niwt-primary/80">
                        <FileText className="w-4 h-4 mr-1" />
                        {control.evidence.length} documents
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Compliance Timeline */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-900 font-libre mb-4">Upcoming Audits</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <Calendar className="w-5 h-5 text-purple-600 mr-3" />
              <div>
                <h4 className="font-medium text-gray-900">Q2 2024 Audit</h4>
                <p className="text-sm text-gray-600">Internal security assessment</p>
              </div>
            </div>
            <span className="text-sm text-gray-500">April 15, 2024</span>
          </div>
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <Calendar className="w-5 h-5 text-purple-600 mr-3" />
              <div>
                <h4 className="font-medium text-gray-900">Annual SOC2 Review</h4>
                <p className="text-sm text-gray-600">External auditor assessment</p>
              </div>
            </div>
            <span className="text-sm text-gray-500">June 1, 2024</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComplianceReport;